import { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries } from 'lightweight-charts';
import { getValidToken } from '../utils/auth';
import { baseChartOptions } from '../utils/chartUtils';

const API_URL = process.env.REACT_APP_API_URL || '';
const CHART_HEIGHT = 300;

function parseContractSymbol(symbol) {
  const m = symbol?.match(/^([A-Z.]+)(\d{6})([CP])(\d{8})$/);
  if (!m) return null;
  const [, underlying, yymmdd, cp, strikeRaw] = m;
  const yy = yymmdd.slice(0, 2), mm = yymmdd.slice(2, 4), dd = yymmdd.slice(4, 6);
  return {
    underlying, expiry: `${mm}/${dd}/20${yy}`,
    type: cp === 'C' ? 'CALL' : 'PUT', strike: parseInt(strikeRaw, 10) / 1000,
  };
}

// Review chart for one Last30Bot trade (same lightweight pattern as
// BacktestContractChart, different data source: /api/last30/contract-bars
// fetches the day's 1-min bars straight from Alpaca on demand). When a
// trade is selected the entry / SL / TS / exit levels are drawn as price
// lines so the whole trade can be judged at a glance.
export default function Last30ContractChart({ contract }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const priceLinesRef = useRef([]);
  const mountedRef = useRef(true);
  const [error, setError] = useState(null);
  const [empty, setEmpty] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    const opts = baseChartOptions(containerRef.current.clientWidth || 300);
    opts.height = CHART_HEIGHT;
    const chart = createChart(containerRef.current, opts);
    chartRef.current = chart;
    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a', downColor: '#ef5350',
      borderUpColor: '#26a69a', borderDownColor: '#ef5350',
      wickUpColor: '#26a69a', wickDownColor: '#ef5350',
    });
    seriesRef.current = series;

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w > 0 && mountedRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: w });
      }
    });
    ro.observe(containerRef.current);

    return () => {
      mountedRef.current = false;
      ro.disconnect();
      chart.remove();
      chartRef.current = seriesRef.current = null;
      priceLinesRef.current = [];
    };
  }, []);

  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return undefined;
    // Clear previous trade's price lines before drawing the next selection.
    priceLinesRef.current.forEach((pl) => series.removePriceLine(pl));
    priceLinesRef.current = [];
    if (!contract?.symbol || !contract?.date) {
      series.setData([]);
      setEmpty(false);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        const token = await getValidToken();
        const res = await fetch(
          `${API_URL}/api/last30/contract-bars?date=${contract.date}&symbol=${encodeURIComponent(contract.symbol)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const bars = await res.json();
        if (cancelled || !mountedRef.current || !seriesRef.current) return;
        series.setData(bars || []);
        setEmpty(!bars || bars.length === 0);
        if (bars && bars.length) {
          chartRef.current?.timeScale().fitContent();
          const t = contract.trade;
          if (t) {
            const lines = [
              { price: t.entry_price, color: '#60a5fa', title: 'entry' },
              { price: t.sl_price, color: '#ef4444', title: 'SL' },
            ];
            if (t.ts_stop > 0) lines.push({ price: t.ts_stop, color: '#22c55e', title: 'TS' });
            if (t.exit_price != null) lines.push({ price: t.exit_price, color: '#f59e0b', title: 'exit' });
            priceLinesRef.current = lines.map((ln) => series.createPriceLine({
              price: ln.price, color: ln.color, lineWidth: 1, lineStyle: 2,
              axisLabelVisible: true, title: ln.title,
            }));
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [contract]);

  const parsed = parseContractSymbol(contract?.symbol);

  return (
    <div className="bg-gray-900 rounded-2xl p-4 mb-3 border" style={{ borderColor: '#1f2937' }}>
      <div className="text-xs text-gray-400 mb-2 font-mono">
        {contract?.symbol ? (
          parsed
            ? `${contract.symbol} · ${parsed.type} ${parsed.strike % 1 === 0 ? parsed.strike.toFixed(0) : parsed.strike.toFixed(1)} · ${parsed.expiry}`
            : contract.symbol
        ) : (
          <span className="text-gray-600">Click a trade below to see its chart with entry / SL / TS / exit levels</span>
        )}
      </div>
      {error && <div className="text-xs text-red-400 mb-2">Failed to load chart: {error}</div>}
      {empty && !error && <div className="text-xs text-gray-600 mb-2">No bars for this contract/day</div>}
      <div ref={containerRef} style={{ width: '100%', height: CHART_HEIGHT }} />
    </div>
  );
}
